import { DBCampaignThunks, campaignSelectors } from '@common-reducers/DBServiceThunks';
import { clearAllPendingCampaignMessagesThunk } from '@common-reducers/CampaignThunks';
import { duplicateCampaignThunk } from '@common-reducers/CampaignReducer';
import { standaloneEditTargetFor, type StandaloneEditTarget } from '../campaigns-redesign/campaign-editability';
import { planEditMenuSelect } from './campaign-edit-menu-select';
import { useNavigateCampaigns, useOpenCampaignPage } from '@common-views/campaigns-redesign/open-campaign-page';
import { ProductEvent, trackProductEvent } from '@common-services/product-analytics';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { CampaignStatus } from '@common-models/campaign-model';
import { Copy, ClipboardList, Pause, Play, Square, Info, Pencil, Repeat } from 'lucide-react';
import { isRecurringCampaign, stopRecurrenceSchedulePatch } from '@common-reducers/campaign-series';
import { useState } from 'react';
import BulkDBFinish from './finish/bulk-db-finish';
import {
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { MessageConfirmationDialog } from '@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

type Props = {
    campaignId: string;
    shouldShowViewLogs?: boolean;
    isCampaignDuplicateShow?: boolean;
    isCampaignSummaryShow?: boolean;
    /** @deprecated No longer changes anything: every action routes the campaigns
     *  surface the same way wherever the menu is opened from. */
    fromCalendar?: boolean;
    onMenuClose?: () => void;
}

export default function CampaignRowMenu({ campaignId, shouldShowViewLogs = true, isCampaignDuplicateShow = true, isCampaignSummaryShow = true, onMenuClose }: Props) {
    const dispatch = useAppDispatch();
    const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
    const { t } = useTranslation(namespace);

    const campaign = useAppSelector(state => campaignSelectors.selectById(state, campaignId))

    // Both of these destroy pending work with no undo, so neither fires on a
    // single click any more: the menu item opens a confirmation that names the
    // consequence, and only the confirm actually runs the action.
    const [pendingAction, setPendingAction] = useState<null | 'abort' | 'stopRecurring'>(null);

    // The campaigns surface is ROUTE-driven (the extension overlay and the
    // website alike). View logs and Duplicate used to set the legacy
    // activeBulkView flags and open the overlay — which the redesign never reads,
    // so the overlay opened on whatever stale route it was last left at — and on
    // the website they went to `/launch-campaign`, which now redirects to
    // `/campaigns/new` and so CREATED an empty draft campaign.
    const navigateCampaigns = useNavigateCampaigns();

    const handleViewLogsClick = () => {
        if (!campaign?._id) return;
        navigateCampaigns(`/campaigns/${campaign._id}/logs`);
    };

    const handleChangeCampaignStatus = (type: CampaignStatus) => {
        dispatch(DBCampaignThunks.patch({
            entity: {
                ...campaign,
                campaignStatus: type,
            }
        }));

        // Track campaign status change
        trackProductEvent(ProductEvent.UpdateCampaign, {
            category: "campaign",
            action: "status_change",
            campaign_id: campaign._id,
            new_status: type,
        });
    };

    const handleAbortCampaign = () => {
        dispatch(clearAllPendingCampaignMessagesThunk({ campaignId: campaign._id }));
        //dispatch(getMessagesAckAndCalculateStatisticsThunk(campaign._id));

        // Track campaign abort
        trackProductEvent(ProductEvent.CancelCampaign, {
            category: "campaign", 
            action: "abort_campaign",
            campaign_id: campaign._id,
        });
    };

    // FE#240: STOP a recurring campaign series. Abort the current occurrence
    // (aborted != complete → the ORCH#102 relaunch gate fails) AND clear
    // schedule.isRecurring (belt-and-braces against any already-spawned successor).
    const handleStopRecurringSeries = () => {
        dispatch(clearAllPendingCampaignMessagesThunk({ campaignId: campaign._id }));
        dispatch(DBCampaignThunks.patch({
            entity: { _id: campaign._id, schedule: stopRecurrenceSchedulePatch(campaign.schedule) },
        }));
        trackProductEvent(ProductEvent.CancelCampaign, {
            category: "campaign",
            action: "stop_recurring_series",
            campaign_id: campaign._id,
        });
    };

    // Open a destructive confirmation from a menu item. Closes the Radix menu
    // the SAME way the edit item does (planEditMenuSelect / D12) — otherwise a
    // controlled caller's onOpenChange unmounts this component and takes the
    // just-opened confirmation with it, and an uncontrolled one leaves the menu
    // open behind the dialog where its DismissableLayer dismisses it on the
    // first click.
    const openDestructiveConfirm = (
        e: { preventDefault: () => void },
        action: 'abort' | 'stopRecurring',
    ) => {
        const plan = planEditMenuSelect(Boolean(onMenuClose));
        if (plan.preventDefaultClose) {
            e.preventDefault();
        }
        setPendingAction(action);
        if (plan.callOnMenuClose) {
            onMenuClose?.();
        }
    };

    // The confirmation hands back `undefined` on Cancel and a string on confirm.
    const handleDestructiveConfirmClose = (confirmed?: string) => {
        const action = pendingAction;
        setPendingAction(null);
        if (confirmed === undefined || !action) return;
        if (action === 'abort') handleAbortCampaign();
        else handleStopRecurringSeries();
    };

    const handleSummaryClick = () => {
        // Open the summary modal
        setIsSummaryModalOpen(true);
        // Close the dropdown menu if callback is provided
        if (onMenuClose) {
            onMenuClose();
        }
    };

    const handleModalClose = () => {
        setIsSummaryModalOpen(false);
    };

    const handleDuplicateCampaign = async () => {
        if (!campaign?._id) return;
        // The copy is a new draft in the campaigns list — go there so it is seen.
        await dispatch(duplicateCampaignThunk(campaign._id));
        navigateCampaigns('/campaigns');
    };

    // #103 / FE#214: Edit is offered on the SAME editability tiers as the
    // redesign — a PENDING campaign and an IN-FLIGHT one, not a finished one.
    // It opens the campaign's PAGE, which is where a campaign is edited: its
    // messages (an in-flight one's through the server-driven editor), and —
    // until the first message is sent — its audience and send time. (It used to
    // open the "Edit scheduled campaign" popup, which the page replaced.)
    const editTarget: StandaloneEditTarget = standaloneEditTargetFor(campaign?.campaignStatus);
    const openCampaignPage = useOpenCampaignPage();

    const handleEditCampaign = () => {
        if (!campaign?._id) return;
        openCampaignPage(campaign.dripId ? String(campaign.dripId) : String(campaign._id));
    };

    return (
        <>
            <DropdownMenuContent 
                className="p-1 min-w-[245px] z-[10000] bg-white dark:bg-slate-800"
                align="end"
                sideOffset={4}
                style={{ 
                    zIndex: 10000,
                    backgroundColor: 'var(--color-surface, #ffffff)',
                    color: 'var(--color-text, #1a202c)',
                    borderColor: 'var(--color-border, #e5e7eb)',
                }}
            >
                {(shouldShowViewLogs ?? true) && (
                    <DropdownMenuItem 
                        onClick={handleViewLogsClick} 
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                    >
                        <ClipboardList className="h-4 w-4" />
                        <span className="ml-1.25">{t('campaignActions.viewLogs')}</span>
                    </DropdownMenuItem>
                )}

                {editTarget && (
                    <DropdownMenuItem
                        onSelect={(e) => {
                            // D12: open the campaign page AND close the action menu —
                            // a menu left open over the page it navigated to would
                            // hold react-remove-scroll's body lock. HOW to close
                            // depends on the caller (see planEditMenuSelect):
                            //  - controlled callers (calendar/sidebar): prevent
                            //    Radix's own close (on the calendar it unmounts this
                            //    component via handleCloseMenu) and close through the
                            //    controlled `onMenuClose` setter instead;
                            //  - uncontrolled caller (bulk dashboard): let Radix
                            //    close the menu normally.
                            const plan = planEditMenuSelect(Boolean(onMenuClose));
                            if (plan.preventDefaultClose) {
                                e.preventDefault();
                            }
                            handleEditCampaign();
                            if (plan.callOnMenuClose) {
                                onMenuClose?.();
                            }
                        }}
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                    >
                        <Pencil className="h-4 w-4" />
                        <span className="ml-1.25">{t('campaignActions.edit')}</span>
                    </DropdownMenuItem>
                )}

                {isCampaignDuplicateShow && (
                    <DropdownMenuItem 
                        onClick={handleDuplicateCampaign} 
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                    >
                        <Copy className="h-4 w-4" />
                        <span className="ml-1.25">{t('campaignActions.duplicate')}</span>
                    </DropdownMenuItem>
                )}

                {campaign?.campaignStatus === 'running' && (
                    <DropdownMenuItem
                        onClick={() => handleChangeCampaignStatus('paused')}
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                    >
                        <Pause className="h-4 w-4" />
                        <span className="ml-1.25">{t('campaignActions.pause')}</span>
                    </DropdownMenuItem>
                )}

                {campaign?.campaignStatus === 'paused' && (
                    <DropdownMenuItem
                        onClick={() => handleChangeCampaignStatus('running')}
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                    >
                        <Play className="h-4 w-4" />
                        <span className="ml-1.25">{t('campaignActions.resume')}</span>
                    </DropdownMenuItem>
                )}

                {!(['complete-delivered', 'aborted'].includes(campaign?.campaignStatus)) && (
                    <DropdownMenuItem
                        onSelect={(e) => openDestructiveConfirm(e, 'abort')}
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)] text-red-600 dark:text-red-400"
                    >
                        <Square className="h-4 w-4 text-red-600 dark:text-red-400" />
                        <span className="ml-1.25">{t('campaignActions.abort')}</span>
                    </DropdownMenuItem>
                )}

                {isRecurringCampaign(campaign) && (
                    <DropdownMenuItem
                        onSelect={(e) => openDestructiveConfirm(e, 'stopRecurring')}
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)] text-red-600 dark:text-red-400"
                    >
                        <Repeat className="h-4 w-4 text-red-600 dark:text-red-400" />
                        <span className="ml-1.25">{t('campaignActions.stopRecurring')}</span>
                    </DropdownMenuItem>
                )}

                {isCampaignSummaryShow && (
                    <DropdownMenuItem 
                        onSelect={(e) => {
                            // Prevent default close behavior to keep menu open
                            e.preventDefault();
                            // Open the summary modal
                            handleSummaryClick();
                        }}
                        className="focus:bg-[var(--color-surface-muted)] dark:focus:bg-[var(--color-surface-muted)]"
                    >
                        <Info className="h-4 w-4" />
                        <span className="ml-1.25">{t('campaignActions.summary')}</span>
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>

            {pendingAction && (
                <MessageConfirmationDialog
                    title={t(pendingAction === 'abort'
                        ? 'campaignActions.confirmAbort.title'
                        : 'campaignActions.confirmStopRecurring.title')}
                    description={t(pendingAction === 'abort'
                        ? 'campaignActions.confirmAbort.body'
                        : 'campaignActions.confirmStopRecurring.body')}
                    confirmLabel={t('campaignActions.confirmAbort.confirm')}
                    destructive={true}
                    shouldShowCancelButton={true}
                    onClose={handleDestructiveConfirmClose}
                />
            )}

            <Dialog open={isSummaryModalOpen} onOpenChange={handleModalClose}>
                <DialogContent 
                    className="p-7 rounded-lg"
                    style={{
                        backgroundColor: 'var(--tasks-board-bg)',
                        width: '700px',
                        maxWidth: '90vw',
                    }}
                >
                    <BulkDBFinish campaignId={campaign?._id} />
                </DialogContent>
            </Dialog>

        </>
    )
}